import React, { useState, useRef, useEffect } from 'react';
import { sendDirectMessage } from './directMessage';
import './DirectMessageWindow.scss';
import { useSelector } from 'react-redux';
import HongButton from '../../comm/HongButton';
import fnc from '../../mymod/commFunction';
import talkIcon from '../../images/home/talk.png';
import smsIcon from '../../images/home/sms.png';
import SmsResult from '../../page_smsResult/SmsResult';
import * as S from '../../scss/theme';
import { getCanonicalTalkUserId, getParentTalkIdByPhone } from '../talkUserId';

const SMS_LIMIT_BYTES = 90;
const MMS_MAX_IMAGE_SIZE = 2000;
const MMS_JPEG_QUALITY = 0.92;

const DirectMessageWindow = ({ selectedStudents, isOpen, onClose, onStudentRemove }) => {
    const [message, setMessage] = useState('');
    const [talkSending, setTalkSending] = useState(false);
    const [smsSending, setSmsSending] = useState(false);
    const [results, setResults] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageSizeNotice, setImageSizeNotice] = useState('');
    const [talkTargets, setTalkTargets] = useState({ student: true, parent: false });
    const [isSmsResultOpen, setIsSmsResultOpen] = useState(false);
    const fileInputRef = useRef(null);
    
    const userInfo = useSelector((state) => state.reduxStore.userInfo);
    const userInfo2 = useSelector((state) => state.reduxStore.userInfo2);
    
    // 디버깅용 useEffect
    useEffect(() => {
        console.log('DirectMessageWindow - selectedStudents 변경:', selectedStudents);
        console.log('DirectMessageWindow - userInfo:', userInfo);
        console.log('DirectMessageWindow - userInfo2:', userInfo2);
    }, [selectedStudents, userInfo, userInfo2]);

  
    
    const certCode = fnc.getCookie('certCode') || '';
    const acaCode = certCode;
    const talkAcademyCode = userInfo2?.acaCode || fnc.getCookie('acaCode') || acaCode;
    
    // 발신자 정보 (현재 로그인한 사용자)
    const SENDER_ID = getCanonicalTalkUserId(userInfo2, userInfo, talkAcademyCode);
    const SENDER_NAME = userInfo?.name || '';
    const chatRoomName = `AI K-EDU 학원`;
    const sendPhone = process.env.REACT_APP_sendPhone || '';
    const smsFolder = process.env.REACT_APP_smsFolder || 'public/sms';
    const isSending = talkSending || smsSending;

    const nowString = () => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const mi = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    };

    const normalizePhone = (value) => String(value || '').replace(/[^0-9]/g, '');

    const sendAxiosPromise = (url, payload) => new Promise((resolve) => {
        fnc.sendAxios(url, payload, (res) => resolve(res));
    });

    const getParentTalkId = (student) => {
        const parentPhone = normalizePhone(student?.parentPhone || student?.hpParent || student?.hp_parent);
        return getParentTalkIdByPhone(parentPhone, talkAcademyCode);
    };

    const getTalkRecipients = () => {
        if (!selectedStudents || selectedStudents.length === 0) return [];

        const recipients = [];
        selectedStudents.forEach((student) => {
            if (talkTargets.student) {
                recipients.push({
                    kind: 'student',
                    label: '학생',
                    receiverId: `${acaCode}_${student.id}`,
                    displayName: student.name,
                    phone: student.hp,
                    student
                });
            }

            if (talkTargets.parent) {
                const parentPhone = normalizePhone(student?.parentPhone || student?.hpParent || student?.hp_parent);
                const parentId = getParentTalkId(student);
                if (parentPhone && parentId) {
                    recipients.push({
                        kind: 'parent',
                        label: '학부모',
                        receiverId: parentId,
                        displayName: student.parentName || `${student.name} 학부모`,
                        phone: student.hpParent || student.parentPhone || '',
                        parentPhone,
                        student
                    });
                }
            }
        });

        return recipients;
    };

    const ensureGuardianTalkTargets = async (recipients) => {
        const guardians = recipients
            .filter((recipient) => recipient.kind === 'parent')
            .map((recipient) => ({
                studentId: recipient.student?.id || '',
                studentStCode: recipient.student?.stCode || 0,
                studentName: recipient.student?.name || '',
                guardianId: recipient.receiverId,
                guardianName: recipient.displayName,
                guardianPhone: recipient.parentPhone,
                relation: 'parent',
                status: 'pending',
                isPrimary: 'Y'
            }));

        const uniqueGuardians = guardians.filter((guardian, index, array) =>
            index === array.findIndex((item) =>
                item.studentId === guardian.studentId && item.guardianId === guardian.guardianId
            )
        );

        if (uniqueGuardians.length === 0) return { success: true };
        return sendAxiosPromise('/socialLoginRoutes/upsert/guardianTalkTargets', {
            acaCode,
            guardians: uniqueGuardians
        });
    };

    const getTalkOptions = (recipient) => {
        if (recipient.kind !== 'parent') return {};
        const studentId = recipient.student?.id || '';
        const studentName = recipient.student?.name || '';
        return {
            targetRole: 'parent',
            contextType: 'student',
            contextId: studentId,
            contextKey: `student_${studentId}`,
            studentId,
            studentName,
            roomName: `${chatRoomName} - ${studentName} 학부모`
        };
    };

    const sanitizeFileName = (name) => String(name || 'image')
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_+/g, '_');

    const loadImageFromFile = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('이미지 로딩 실패'));
            img.src = reader.result;
        };
        reader.onerror = () => reject(new Error('파일 읽기 실패'));
        reader.readAsDataURL(file);
    });

    const formatFileSize = (bytes) => {
        const size = Number(bytes || 0);
        if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;
        return `${(size / 1024).toFixed(1)} KB`;
    };

    const getImageSizeNotice = (file, image) => {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        const needsResize = Math.max(width, height) > MMS_MAX_IMAGE_SIZE;
        const fileSize = formatFileSize(file?.size);

        if (needsResize) {
            const scale = MMS_MAX_IMAGE_SIZE / Math.max(width, height);
            const resizedWidth = Math.max(1, Math.round(width * scale));
            const resizedHeight = Math.max(1, Math.round(height * scale));
            return `문자사 이미지 기준(${MMS_MAX_IMAGE_SIZE}x${MMS_MAX_IMAGE_SIZE}) 초과: 원본 ${width}x${height}, ${fileSize}. 전송 시 ${resizedWidth}x${resizedHeight} JPG로 자동 축소됩니다.`;
        }

        return `문자사 이미지 기준 통과: ${width}x${height}, ${fileSize}. 전송 시 JPG로 변환됩니다.`;
    };

    const convertToMmsJpegFile = async (file) => {
        const image = await loadImageFromFile(file);
        const originalWidth = image.naturalWidth || image.width;
        const originalHeight = image.naturalHeight || image.height;
        const scale = Math.min(1, MMS_MAX_IMAGE_SIZE / Math.max(originalWidth, originalHeight));
        const width = Math.max(1, Math.round(originalWidth * scale));
        const height = Math.max(1, Math.round(originalHeight * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('이미지 변환 컨텍스트 생성 실패');
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(image, 0, 0, width, height);

        const jpegBlob = await new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), 'image/jpeg', MMS_JPEG_QUALITY);
        });

        if (!jpegBlob) {
            throw new Error('JPG 변환 실패');
        }

        const baseName = sanitizeFileName(String(file?.name || 'mms_image').replace(/\.[^/.]+$/, ''));
        return new File([jpegBlob], `${baseName}.jpg`, { type: 'image/jpeg' });
    };

    const uploadImageToS3ForMms = async () => {
        if (!selectedImage) return '';

        const mmsImage = await convertToMmsJpegFile(selectedImage);
        const baseName = sanitizeFileName(String(mmsImage.name || 'image').replace(/\.[^/.]+$/, ''));
        const s3FileName = `${fnc.fncSirialNum('S')}_${baseName}.jpg`;
        const s3Key = `public/sms/${s3FileName}`;

        const formData = new FormData();
        formData.append('file', mmsImage, `${baseName}.jpg`);
        formData.append('s3Key', s3Key);

        const uploadRes = await sendAxiosPromise('/uploadS3', formData);
        if (!uploadRes) {
            throw new Error('S3 업로드 실패');
        }

        return `${smsFolder}/${s3FileName}`;
    };

    // 이미지 선택
    const handleImageSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 선택할 수 있습니다.');
            setImageSizeNotice('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        try {
            const image = await loadImageFromFile(file);
            const notice = getImageSizeNotice(file, image);
            setImageSizeNotice(notice);

            if (Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height) > MMS_MAX_IMAGE_SIZE) {
                alert(notice);
            }

            setSelectedImage(file);
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } catch (error) {
            console.error('이미지 크기 확인 실패:', error);
            alert('이미지 크기를 확인할 수 없습니다. 다른 이미지를 선택해주세요.');
            handleImageCancel();
        }
    };

    // 이미지 선택 취소
    const handleImageCancel = () => {
        setSelectedImage(null);
        setImagePreview(null);
        setImageSizeNotice('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 일괄 텍스트 메시지 전송
    const handleSendBulkMessage = async () => {
        console.log('handleSendBulkMessage 호출됨');
        console.log('message:', message);
        console.log('selectedStudents:', selectedStudents);
        
        if (!message.trim()) {
            alert('메시지를 입력해주세요.');
            return;
        }

        if (!selectedStudents || selectedStudents.length === 0) {
            alert('선택된 학생이 없습니다.');
            return;
        }

        const recipients = getTalkRecipients();
        if (recipients.length === 0) {
            alert('전송할 톡 대상을 선택해주세요. 학부모 톡은 학부모 휴대폰이 있는 학생만 전송할 수 있습니다.');
            return;
        }

        console.log('전송 시작');
        setTalkSending(true);
        setResults([]);

        const sendResults = [];

        try {
            const guardianSyncResult = await ensureGuardianTalkTargets(recipients);
            if (guardianSyncResult && guardianSyncResult.failed && guardianSyncResult.failed.length > 0) {
                console.warn('일부 학부모 매칭 저장 실패:', guardianSyncResult.failed);
            }

            for (const recipient of recipients) {
                try {
                    const response = await sendDirectMessage(
                        chatRoomName,
                        SENDER_ID,
                        SENDER_NAME,
                        recipient.receiverId,
                        message,
                        'text',
                        null,
                        getTalkOptions(recipient)
                    );

                    sendResults.push({
                        student: recipient.student,
                        targetLabel: recipient.label,
                        targetName: recipient.displayName,
                        targetKind: recipient.kind,
                        success: response.success,
                        message: response.success ? '전송 성공' : `전송 실패: ${response.error}`,
                        messageId: response.messageId,
                        chatRoomId: response.chatRoomId
                    });
                } catch (error) {
                    sendResults.push({
                        student: recipient.student,
                        targetLabel: recipient.label,
                        targetName: recipient.displayName,
                        targetKind: recipient.kind,
                        success: false,
                        message: `전송 실패: ${error.message}`
                    });
                }

                // 각 전송 후 잠시 대기 (Firebase 부하 방지)
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            setResults(sendResults);
            
            const successCount = sendResults.filter(r => r.success).length;
            const totalCount = sendResults.length;
            
            if (successCount === totalCount) {
                alert(`모든 메시지가 성공적으로 전송되었습니다. (${successCount}/${totalCount})`);
                setMessage('');
            } else {
                alert(`일부 메시지 전송에 실패했습니다. (성공: ${successCount}/${totalCount})`);
            }

        } catch (error) {
            console.error('일괄 메시지 전송 중 오류:', error);
            alert('메시지 전송 중 오류가 발생했습니다.');
        } finally {
            setTalkSending(false);
        }
    };
    const handleSendBulkSmsMessage = async () => {
        if (!selectedStudents || selectedStudents.length === 0) {
            alert('선택된 학생이 없습니다.');
            return;
        }

        if (!message.trim() && !selectedImage) {
            alert('메시지를 입력하거나 이미지를 첨부해주세요.');
            return;
        }

        const confirmMessage = selectedImage
            ? `선택한 ${selectedStudents.length}명에게 MMS를 전송하시겠습니까?`
            : `선택한 ${selectedStudents.length}명에게 문자를 전송하시겠습니까?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setSmsSending(true);
        setResults([]);

        try {
            let imageUrl = '';
            const isMms = Boolean(selectedImage);
            if (isMms) {
                imageUrl = await uploadImageToS3ForMms();
            }

            const sendDate = nowString();
            const smsMessageText = String(message || '');
            const smsFinalMessageText = `${smsMessageText} [AIKEDU]`;
            const byteSize = fnc.getByte(smsFinalMessageText);
            const msgType = byteSize > SMS_LIMIT_BYTES ? 6 : 0; // 0:SMS, 6:LMS

            const sendResults = [];

            for (const student of selectedStudents) {
                const recivePhone = normalizePhone(student?.hp);
                if (!recivePhone || recivePhone.length < 10) {
                    sendResults.push({
                        student,
                        success: false,
                        message: '전송 실패: 전화번호 없음'
                    });
                    continue;
                }

                let res;
                if (isMms) {
                    const payload = {
                        SUBJECT: '직접메시지',
                        MSG: String(message || '이미지'),
                        FILE_CNT: 1,
                        FILE_PATH1: imageUrl,
                        PHONE: recivePhone,
                        sendPhone,
                        sendDateTime: sendDate,
                        acaCode,
                        stCode: student?.stCode || '',
                        stName: student?.name || student?.stName || '',
                        mode: '즉시',
                        sendUserName: SENDER_NAME || '',
                        category: '직접메시지'
                    };
                    // eslint-disable-next-line no-await-in-loop
                    res = await sendAxiosPromise('/kuesbSmsRoutes/insert/mms', payload);
                } else {
                    const payload = {
                        recivePhone,
                        senderPhone: sendPhone,
                        msgContent: String(message || ''),
                        originalSenderPhone: sendPhone,
                        acaCode,
                        stCode: student?.stCode || '',
                        stName: student?.name || student?.stName || '',
                        gangsaName: SENDER_NAME || '',
                        category: '직접메시지',
                        sendDate,
                        reservationType: '즉시',
                        msgType
                    };
                    // eslint-disable-next-line no-await-in-loop
                    res = await sendAxiosPromise('/kuesbSmsRoutes/insert/sms', payload);
                }

                const ok = !!(res && res.success);
                sendResults.push({
                    student,
                    success: ok,
                    message: ok ? '전송 성공' : `전송 실패: ${res?.message || '요청 실패'}`
                });

                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => setTimeout(resolve, 80));
            }

            setResults(sendResults);
            const successCount = sendResults.filter((r) => r.success).length;
            const totalCount = sendResults.length;
            alert(`문자 전송 완료 (성공: ${successCount}/${totalCount})`);
        } catch (error) {
            console.error('문자 전송 중 오류:', error);
            alert('문자 전송 중 오류가 발생했습니다.');
        } finally {
            setSmsSending(false);
        }
    };
    const handleSendTalkMessage = async () => {
        if (!selectedStudents || selectedStudents.length === 0) {
            alert('선택된 학생이 없습니다.');
            return;
        }

        if (!message.trim() && !imagePreview) {
            alert('메시지를 입력하거나 이미지를 첨부해주세요.');
            return;
        }

        const recipients = getTalkRecipients();
        if (recipients.length === 0) {
            alert('전송할 톡 대상을 선택해주세요. 학부모 톡은 학부모 휴대폰이 있는 학생만 전송할 수 있습니다.');
            return;
        }

        const confirmMessage = imagePreview
            ? `선택한 톡 대상 ${recipients.length}명에게 이미지를 전송하시겠습니까?`
            : `선택한 톡 대상 ${recipients.length}명에게 톡을 전송하시겠습니까?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        if (imagePreview) {
            await handleSendBulkImageMessage();
            return;
        }
        await handleSendBulkMessage();
    };

    // 일괄 이미지 메시지 전송
    const handleSendBulkImageMessage = async () => {
        if (!selectedImage) {
            alert('이미지를 선택해주세요.');
            return;
        }

        if (!selectedStudents || selectedStudents.length === 0) {
            alert('선택된 학생이 없습니다.');
            return;
        }

        const recipients = getTalkRecipients();
        if (recipients.length === 0) {
            alert('전송할 톡 대상을 선택해주세요. 학부모 톡은 학부모 휴대폰이 있는 학생만 전송할 수 있습니다.');
            return;
        }

        setTalkSending(true);
        setResults([]);

        try {
            // Firebase Storage에 이미지 업로드 (한 번만)
            const { ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { storage } = await import('../firebaseConfig');
            
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(2, 9);
            const fileName = `images/${timestamp}_${randomId}_${selectedImage.name}`;
            const imageRef = storageRef(storage, fileName);
            
            const uploadTask = uploadBytesResumable(imageRef, selectedImage);
            
            const downloadURL = await new Promise((resolve, reject) => {
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`이미지 업로드 진행률: ${progress}%`);
                    },
                    (error) => {
                        console.error('이미지 업로드 실패:', error);
                        reject(error);
                    },
                    async () => {
                        const url = await getDownloadURL(uploadTask.snapshot.ref);
                        resolve(url);
                    }
                );
            });

            const imageData = [{
                url: downloadURL,
                name: selectedImage.name,
                size: selectedImage.size,
                type: 'image'
            }];

            const guardianSyncResult = await ensureGuardianTalkTargets(recipients);
            if (guardianSyncResult && guardianSyncResult.failed && guardianSyncResult.failed.length > 0) {
                console.warn('일부 학부모 매칭 저장 실패:', guardianSyncResult.failed);
            }

            // 각 대상에게 이미지 메시지 전송
            const sendResults = [];

            for (const recipient of recipients) {
                try {
                    const response = await sendDirectMessage(
                        chatRoomName,
                        SENDER_ID,
                        SENDER_NAME,
                        recipient.receiverId,
                        message.trim() || '이미지',
                        'image',
                        imageData,
                        getTalkOptions(recipient)
                    );

                    sendResults.push({
                        student: recipient.student,
                        targetLabel: recipient.label,
                        targetName: recipient.displayName,
                        targetKind: recipient.kind,
                        success: response.success,
                        message: response.success ? '전송 성공' : `전송 실패: ${response.error}`,
                        messageId: response.messageId,
                        chatRoomId: response.chatRoomId
                    });
                } catch (error) {
                    sendResults.push({
                        student: recipient.student,
                        targetLabel: recipient.label,
                        targetName: recipient.displayName,
                        targetKind: recipient.kind,
                        success: false,
                        message: `전송 실패: ${error.message}`
                    });
                }

                await new Promise(resolve => setTimeout(resolve, 100));
            }

            setResults(sendResults);
            
            const successCount = sendResults.filter(r => r.success).length;
            const totalCount = sendResults.length;
            
            if (successCount === totalCount) {
                alert(`모든 이미지가 성공적으로 전송되었습니다. (${successCount}/${totalCount})`);
                setMessage('');
                handleImageCancel();
            } else {
                alert(`일부 이미지 전송에 실패했습니다. (성공: ${successCount}/${totalCount})`);
            }

        } catch (error) {
            console.error('일괄 이미지 전송 중 오류:', error);
            alert('이미지 전송 중 오류가 발생했습니다.');
        } finally {
            setTalkSending(false);
        }
    };
    if (!isOpen) return null;

      // 학생 제거 함수
    const handleRemoveStudent = (studentToRemove) => {
        console.log('학생 제거:', studentToRemove.name);
        // 부모 컴포넌트에 학생 제거 알림
        if (onStudentRemove) {
            onStudentRemove(studentToRemove);
        }
    };

    const talkRecipients = getTalkRecipients();

    //=========================================
    return (
        <>
        <div className="direct-message-window-fixed">
            <div className="window-header">
                <h3>📱 메시지 전송</h3>
            </div>

            <div className="window-content">
                                <div className="recipient-info">
                    <h4>톡 수신 대상 ({talkRecipients.length}명)</h4>
                    <div className="target-options">
                        <label className={`target-option ${talkTargets.student ? 'active' : ''}`}>
                            <input
                                type="checkbox"
                                checked={talkTargets.student}
                                onChange={() => setTalkTargets((prev) => ({ ...prev, student: !prev.student }))}
                                disabled={isSending}
                            />
                            <span className="target-title">학생</span>
                            <span className="target-desc">본인 휴대폰</span>
                        </label>
                        <label className={`target-option ${talkTargets.parent ? 'active' : ''}`}>
                            <input
                                type="checkbox"
                                checked={talkTargets.parent}
                                onChange={() => setTalkTargets((prev) => ({ ...prev, parent: !prev.parent }))}
                                disabled={isSending}
                            />
                            <span className="target-title">학부모</span>
                            <span className="target-desc">학부모 휴대폰</span>
                        </label>
                    </div>
                    <div className="recipient-list">
                        {talkRecipients.length > 0 ? (
                            talkRecipients.map((recipient, index) => (
                                <div key={`${recipient.kind}_${recipient.student?.id || index}_${recipient.receiverId}`} className={`recipient-item ${recipient.kind}`}>
                                    <span className={`target-badge ${recipient.kind}`}>{recipient.label}</span>
                                    <span className="student-name">{recipient.displayName}</span>
                                    <span className="student-id">({recipient.student?.id})</span>
                                    <span className="student-phone">{recipient.phone || '-'}</span>
                                    {recipient.kind === 'parent' && (
                                        <span className="recipient-meta">{recipient.student?.name} 학생</span>
                                    )}
                                    <button
                                        className="remove-student-btn"
                                        onClick={() => handleRemoveStudent(recipient.student)}
                                        title="학생 제거"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="no-students">
                                학생을 선택하거나 톡 대상을 선택해주세요
                            </div>
                        )}
                    </div>
                </div>
                <div className="message-section">
                    <h4>메시지 작성</h4>
                    <textarea
                        className="message-textarea"
                        placeholder="전송할 메시지를 입력하세요..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={isSending}
                        rows={4}
                    />

                    <div className="image-section">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageSelect}
                            accept="image/*"
                            style={{ display: 'none' }}
                        />
                        
                        {!imagePreview ? (
                            <button 
                                className="select-image-button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isSending}
                            >
                                📷 이미지 첨부
                            </button>
                        ) : (
                            <div className="image-preview-container">
                                <div className="image-preview">
                                    <img src={imagePreview} alt="미리보기" />
                                    <button 
                                        className="remove-image-button"
                                        onClick={handleImageCancel}
                                        disabled={isSending}
                                    >
                                        ✕
                                    </button>
                                </div>
                                <div className="image-info">
                                    <p>{selectedImage?.name}</p>
                                    <p>{(selectedImage?.size / 1024).toFixed(2)} KB</p>
                                    {imageSizeNotice && (
                                        <p className="mms-image-notice">{imageSizeNotice}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="send-buttons">
                        <HongButton 
                            className="send-button talk-send h-btn-blue"
                            onClick={handleSendTalkMessage}
                            disabled={talkSending}
                        >
                            {talkSending ? '전송 중...' : (
                                <span className="send-button-content">
                                    <img src={talkIcon} alt="톡" className="send-button-icon" />
                                    톡전송
                                </span>
                            )}
                        </HongButton>

                        <HongButton 
                            className="send-button sms-send h-btn-green"
                            onClick={handleSendBulkSmsMessage}
                            disabled={smsSending}
                        >
                            {smsSending ? '전송 중...' : (
                                <span className="send-button-content">
                                    <img src={smsIcon} alt="문자" className="send-button-icon" />
                                    문자전송
                                </span>
                            )}
                        </HongButton>

                        <HongButton
                            className="send-button sms-history h-btn-gray"
                            onClick={() => setIsSmsResultOpen(true)}
                        >
                            문자내역
                        </HongButton>
                    </div>
                </div>

                {results.length > 0 && (
                    <div className="results-section">
                        <h4>전송 결과</h4>
                        <div className="results-list">
                            {results.map((result, index) => (
                                <div 
                                    key={index} 
                                    className={`result-item ${result.success ? 'success' : 'error'}`}
                                >
                                    <span className={`target-badge ${result.targetKind || 'student'}`}>{result.targetLabel || '학생'}</span>
                                    <span className="student-name">{result.targetName || result.student.name}</span>
                                    <span className="result-message">{result.message}</span>
                                    {result.messageId && (
                                        <span className="message-id">ID: {result.messageId}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>

        {isSmsResultOpen && (
            <S.Modal_fullScreen backgroundColor="white">
                <SmsResult setIsModal={setIsSmsResultOpen} />
            </S.Modal_fullScreen>
        )}
        </>
    );
};

export default DirectMessageWindow;


